import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useGtt, CONDITIONS } from "../lib/gtt";
import { fmt, pct } from "../lib/format";
import { Switch } from "./ui/switch";

const inputCls = "w-full h-9 px-2.5 rounded-md border border-input bg-background num text-[13px] outline-none focus:border-primary";

export function AlertForm({ inst, tick }) {
  const ltp = tick?.ltp || 0;
  const [f, setF] = useState({ condition: "above", value: ltp ? +(ltp * 1.02).toFixed(2) : "", email: true, note: "" });
  const [busy, setBusy] = useState(false);
  const { close, bump } = useGtt();
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const isPct = f.condition.startsWith("pct");
  const choose = (c) => set({ condition: c, value: c.startsWith("pct") ? 2 : ltp ? +(ltp * (c === "above" ? 1.02 : 0.98)).toFixed(2) : "" });
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/alerts", { instrument_key: inst.instrument_key, condition: f.condition, value: Number(f.value), email: f.email, note: f.note });
      toast.success(`Alert set · ${inst.trading_symbol}`, { description: f.email ? "You'll get an in-app notification and an email." : "You'll get an in-app notification." });
      bump(); close();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="alert-form">
      <div className="text-[11px] num text-muted-foreground">LTP <span className="text-foreground font-medium">₹{fmt(ltp)}</span> · Today {pct(tick?.chp)}</div>
      <div className="grid grid-cols-2 gap-1.5">
        {CONDITIONS.map(([v, l, u]) => (
          <button key={v} data-testid={`alert-condition-${v}`} onClick={() => choose(v)}
            className={`h-8 rounded-md border text-[11.5px] font-medium transition-colors duration-150 ${f.condition === v ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}{u === "%" ? "%" : ""}</button>
        ))}
      </div>
      <label className="block"><span className="eyebrow block mb-1">{isPct ? "Percent change (day)" : "Price ₹"}</span>
        <input data-testid="alert-value-input" type="number" step={isPct ? 0.1 : inst.tick_size || 0.05} value={f.value} onChange={(e) => set({ value: e.target.value })} className={inputCls} /></label>
      <label className="block"><span className="eyebrow block mb-1">Note (optional)</span>
        <input data-testid="alert-note-input" maxLength={120} value={f.note} onChange={(e) => set({ note: e.target.value })} className={`${inputCls} font-sans`} placeholder="e.g. Breakout above resistance" /></label>
      <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
        <div className="text-[12px]">Email me too<span className="block text-[10.5px] text-muted-foreground">Sent to your login email</span></div>
        <Switch data-testid="alert-email-switch" checked={f.email} onCheckedChange={(email) => set({ email })} />
      </div>
      <button data-testid="alert-submit-button" disabled={busy} onClick={submit}
        className="w-full h-10 rounded-md bg-primary text-primary-foreground font-semibold text-[13px] flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.99] transition-[filter,transform] duration-150 disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}Create alert
      </button>
    </div>
  );
}
